'use client'

import { useTheme } from '@/components/theme-provider'
import { X } from '@/components/icons'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import { Toaster as Sonner, toast, useSonner } from 'sonner'

type ToasterProps = React.ComponentProps<typeof Sonner>

/**
 * Sonner keeps the hard parts — positioning, stacking, enter/exit, swipe to
 * dismiss, hover-pause and the dismiss timer. Its own visual shell is stripped
 * here so `<ToastCard>` is the only toast surface in the app; see
 * `components/ui/toast-card.tsx` for why the card can't live on sonner's root.
 */
const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = 'system' } = useTheme()
  const t = useTranslations('Toast')
  const { toasts } = useSonner()
  const safeTheme = theme === 'light' || theme === 'dark' || theme === 'system' ? theme : 'system'
  const activeToastCount = toasts.filter((item) => !item.toasterId).length

  return (
    <div
      className={cn(
        'group/toaster fixed end-6 top-6 z-[var(--z-toast)] w-[356px] max-sm:start-4 max-sm:end-4 max-sm:top-4 max-sm:w-auto',
        activeToastCount > 1 ? 'pointer-events-auto' : 'pointer-events-none'
      )}
    >
      <div className="flex h-9 items-start justify-end">
        {activeToastCount > 1 ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            feedback="none"
            onClick={() => toast.dismiss()}
            className="surface-elevated text-destructive hover:text-destructive pointer-events-none opacity-0 transition-opacity duration-[var(--duration-fast)] group-focus-within/toaster:pointer-events-auto group-focus-within/toaster:opacity-100 group-hover/toaster:pointer-events-auto group-hover/toaster:opacity-100 max-sm:pointer-events-auto max-sm:opacity-100"
          >
            <X className="!size-3.5" aria-hidden />
            {t('dismissAll')}
          </Button>
        ) : null}
      </div>

      <Sonner
        data-slot="sonner"
        theme={safeTheme}
        className="toaster group trellis-product-ui !pointer-events-auto !relative !inset-auto !z-auto !w-full"
        position="top-right"
        offset={0}
        mobileOffset={0}
        // Set explicitly rather than inherited: repeats collapse onto one card,
        // so the cap only ever governs genuinely distinct toasts.
        visibleToasts={3}
        expand={false}
        // The card draws its own close button, inside the bumping surface.
        closeButton={false}
        duration={4000}
        toastOptions={{
          classNames: {
            toast:
              'trellis-product-ui group toast !border-0 !bg-transparent !p-0 !shadow-none group-[.toaster]:!border-0 group-[.toaster]:!bg-transparent group-[.toaster]:!shadow-none',
            content: '!m-0 !p-0 w-full',
            title: '!m-0 !p-0 w-full !text-inherit !font-normal',
          },
        }}
        {...props}
      />
    </div>
  )
}

export { Toaster }
